import React from 'react';
import { Globe2, Shield, Target, Filter, ChevronDown, ChevronUp, X, Heart } from 'lucide-react';
import { budgetCountries } from '../../data/budget-countries';

interface MatchingFiltersProps {
  onFilterChange: (filters: { destination?: string, pvtStatus?: string, objective?: string, likedOnly?: boolean }) => void;
  currentUserId?: string;
}

export function MatchingFilters({ onFilterChange, currentUserId }: MatchingFiltersProps) {
  const [selectedDestination, setSelectedDestination] = React.useState<string>('');
  const [selectedPvtStatus, setSelectedPvtStatus] = React.useState<string>('');
  const [selectedObjective, setSelectedObjective] = React.useState<string>('');
  const [showLikedOnly, setShowLikedOnly] = React.useState<boolean>(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [activeFilterCount, setActiveFilterCount] = React.useState(0);

  // Calculate active filter count
  React.useEffect(() => {
    let count = 0;
    if (selectedDestination) count++;
    if (selectedPvtStatus) count++;
    if (selectedObjective) count++;
    if (showLikedOnly) count++;
    setActiveFilterCount(count);
  }, [selectedDestination, selectedPvtStatus, selectedObjective, showLikedOnly]);

  const handleDestinationChange = (destination: string) => {
    setSelectedDestination(destination);
    // Clear other filters if destination is cleared
    if (!destination) {
      setSelectedPvtStatus('');
      setSelectedObjective('');
    }
    // Pass all filters to maintain all criteria
    onFilterChange({ 
      destination: destination || undefined,
      pvtStatus: selectedPvtStatus || undefined,
      objective: selectedObjective || undefined,
      likedOnly: showLikedOnly || undefined
    });
  };

  const handlePvtStatusChange = (status: string) => {
    // Only allow status selection if destination is selected
    if (!selectedDestination && status) {
      // Show a hint message to the user
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-6 py-3 rounded-xl shadow-lg border border-amber-100 dark:border-amber-800/30 z-50 flex items-center gap-3 animate-fade-in';
      toast.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Veuillez d'abord sélectionner une destination</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
      return;
    }
    
    setSelectedPvtStatus(status);
    // Pass all filters to maintain all criteria
    onFilterChange({ 
      destination: selectedDestination || undefined,
      pvtStatus: status || undefined,
      objective: selectedObjective || undefined,
      likedOnly: showLikedOnly || undefined
    });
  };

  const handleObjectiveChange = (objective: string) => {
    // Only allow objective selection if destination is selected
    if (!selectedDestination && objective) {
      // Show a hint message to the user
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-6 py-3 rounded-xl shadow-lg border border-amber-100 dark:border-amber-800/30 z-50 flex items-center gap-3 animate-fade-in';
      toast.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Veuillez d'abord sélectionner une destination</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
      return;
    }
    
    setSelectedObjective(objective);
    // Pass all filters to maintain all criteria
    onFilterChange({ 
      destination: selectedDestination || undefined,
      pvtStatus: selectedPvtStatus || undefined,
      objective: objective || undefined,
      likedOnly: showLikedOnly || undefined
    });
  };

  const handleLikedOnlyToggle = (value: boolean) => {
    setShowLikedOnly(value);
    onFilterChange({
      destination: selectedDestination || undefined,
      pvtStatus: selectedPvtStatus || undefined,
      objective: selectedObjective || undefined,
      likedOnly: value || undefined
    });
  };

  const resetFilters = () => {
    setSelectedDestination('');
    setSelectedPvtStatus('');
    setSelectedObjective('');
    setShowLikedOnly(false);
    onFilterChange({});
  };

  const PVT_OBJECTIVES = [
    { id: 'language', label: 'Améliorer une langue' },
    { id: 'career', label: 'Faire évoluer sa carrière' },
    { id: 'travel', label: 'Voyager et explorer' },
    { id: 'nature', label: 'Expérience en pleine nature' },
    { id: 'culture', label: 'Découvrir la culture locale & faire la fête' }
  ];

  return (
    <div className="w-full">
      {/* Filter Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 ${activeFilterCount > 0 ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
            <Filter className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {activeFilterCount > 0 
                ? `${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''} actif${activeFilterCount > 1 ? 's' : ''}` 
                : 'Filtrer les profils'}
            </p>
            {(selectedDestination || selectedPvtStatus || selectedObjective) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px] sm:max-w-[300px]">
                {[
                  selectedDestination && budgetCountries.find(c => c.code === selectedDestination)?.name,
                  selectedPvtStatus && (
                    selectedPvtStatus === 'obtained' ? 'PVT obtenu' :
                    selectedPvtStatus === 'pending' ? 'En cours' :
                    'Pas encore demandé'
                  ),
                  selectedObjective && PVT_OBJECTIVES.find(o => o.id === selectedObjective)?.label
                ].filter(Boolean).join(' • ')}
              </p>
            )}
            {showLikedOnly && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px] sm:max-w-[300px]">
                Uniquement les profils likés
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Filters Panel - Hidden by default */}
      <div 
        className={`mt-3 overflow-hidden transition-all duration-300 ${
          isExpanded 
            ? 'max-h-[500px] opacity-100 transform translate-y-0' 
            : 'max-h-0 opacity-0 transform -translate-y-4'
        }`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Critères de recherche
            </h3>
            
            {activeFilterCount > 0 && (
              <button 
                onClick={resetFilters} 
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg border border-rose-100 dark:border-rose-800/30 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                <span>Réinitialiser</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {/* Destination Filter - Always enabled */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Destination
              </label>
              <select
                value={selectedDestination}
                onChange={(e) => handleDestinationChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:text-white text-sm"
              >
                <option value="">Toutes les destinations</option>
                {budgetCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* PVT Status Filter - Disabled if no destination selected */}
            <div className="space-y-2">
              <label className={`block text-sm font-medium mb-1 ${!selectedDestination ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                Statut du PVT
              </label>
              <select
                value={selectedPvtStatus}
                onChange={(e) => handlePvtStatusChange(e.target.value)}
                className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:text-white text-sm ${
                  !selectedDestination ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                aria-describedby={!selectedDestination ? 'pvt-status-help' : undefined}
              >
                <option value="">Tous les statuts</option>
                <option value="obtained">PVT obtenu</option>
                <option value="pending">En cours</option>
                <option value="not-requested">Pas encore demandé</option>
              </select>
              {!selectedDestination && (
                <p id="pvt-status-help" className="text-xs text-amber-500 dark:text-amber-400 mt-1">
                  Sélectionnez d'abord une destination
                </p>
              )}
            </div>

            {/* Objectives Filter - Disabled if no destination selected */}
            <div className="space-y-2">
              <label className={`block text-sm font-medium mb-1 ${!selectedDestination ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                Objectifs du PVT
              </label>
              <select
                value={selectedObjective}
                onChange={(e) => handleObjectiveChange(e.target.value)}
                className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:text-white text-sm ${
                  !selectedDestination ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                aria-describedby={!selectedDestination ? 'objectives-help' : undefined}
              >
                <option value="">Tous les objectifs</option>
                {PVT_OBJECTIVES.map((objective) => (
                  <option key={objective.id} value={objective.id}>
                    {objective.label}
                  </option>
                ))}
              </select>
              {!selectedDestination && (
                <p id="objectives-help" className="text-xs text-amber-500 dark:text-amber-400 mt-1">
                  Sélectionnez d'abord une destination
                </p>
              )}
            </div>
          </div>

          {/* Liked Only Filter */}
          {currentUserId && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Uniquement les profils likés
                </label>
                <div className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer"
                  onClick={() => handleLikedOnlyToggle(!showLikedOnly)}
                  role="switch"
                  aria-checked={showLikedOnly}
                  style={{ backgroundColor: showLikedOnly ? '#EF4136' : '#e5e7eb' }}
                >
                  <span
                    className={`${
                      showLikedOnly ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out`}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Affiche uniquement les profils que vous avez likés
              </p>
            </div>
          )}

          {/* Applied Filters */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
              <div className="flex flex-wrap gap-2">
                {selectedDestination && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg text-sm border border-primary-100/50 dark:border-primary-800/30">
                    <Globe2 className="w-3.5 h-3.5" />
                    <span>{budgetCountries.find(c => c.code === selectedDestination)?.name}</span>
                    <button 
                      onClick={() => handleDestinationChange('')}
                      className="p-0.5 hover:bg-primary-100/80 dark:hover:bg-primary-800/30 rounded-full"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                
                {selectedPvtStatus && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg text-sm border border-primary-100/50 dark:border-primary-800/30">
                    <Shield className="w-3.5 h-3.5" />
                    <span>
                      {selectedPvtStatus === 'obtained' ? 'PVT obtenu' :
                      selectedPvtStatus === 'pending' ? 'En cours' :
                      'Pas encore demandé'}
                    </span>
                    <button 
                      onClick={() => handlePvtStatusChange('')}
                      className="p-0.5 hover:bg-primary-100/80 dark:hover:bg-primary-800/30 rounded-full"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                
                {selectedObjective && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg text-sm border border-primary-100/50 dark:border-primary-800/30">
                    <Target className="w-3.5 h-3.5" />
                    <span>{PVT_OBJECTIVES.find(o => o.id === selectedObjective)?.label}</span>
                    <button 
                      onClick={() => handleObjectiveChange('')}
                      className="p-0.5 hover:bg-primary-100/80 dark:hover:bg-primary-800/30 rounded-full"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                
                {showLikedOnly && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg text-sm border border-primary-100/50 dark:border-primary-800/30">
                    <Heart className="w-3.5 h-3.5" />
                    <span>Profils likés</span>
                    <button 
                      onClick={() => handleLikedOnlyToggle(false)}
                      className="p-0.5 hover:bg-primary-100/80 dark:hover:bg-primary-800/30 rounded-full"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}